import { useMemo } from "react";
import { useTurnoverData, type ApiRequestConfig } from "@repo/api";

interface TurnoverDataPoint {
  date: string;
  turnover: number;
}

interface UseTurnoverResult {
  data: TurnoverDataPoint[];
  meanTurnover?: number;
  statistics?: {
    mean: number;
    median: number;
    std: number;
    min: number;
    max: number;
    count: number;
  };
  isLoading: boolean;
  error: Error | null;
}

/**
 * Hook to fetch and transform turnover data for charting
 * Converts the API response into the format expected by TurnoverChart component
 */
export function useTurnover(config?: ApiRequestConfig): UseTurnoverResult {
  const { data: apiData, isLoading, error } = useTurnoverData(config);

  const transformedData = useMemo(() => {
    if (!apiData || !apiData.turnover_series) {
      return {
        data: [],
        meanTurnover: undefined,
        statistics: undefined,
      };
    }

    // Transform turnover_series object to array of data points
    const dataPoints: TurnoverDataPoint[] = Object.entries(
      apiData.turnover_series
    )
      .map(([date, turnover]) => ({
        date,
        turnover: typeof turnover === "number" ? turnover : 0,
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return {
      data: dataPoints,
      meanTurnover: apiData.mean_turnover,
      statistics: apiData.turnover_statistics,
    };
  }, [apiData]);

  return {
    ...transformedData,
    isLoading,
    error: error as Error | null,
  };
}
